import { X } from "lucide-react";
import { useId, useState, type KeyboardEvent } from "react";
import { cn } from "@/lib/utils";

/**
 * Tags as removable chips. Enter or comma adds, Backspace on an empty input removes
 * the last one, and each change is announced. Without `label`, the caller labels the
 * input through `id` (a form field's own label).
 */
export function TagInput({
  label,
  id,
  value,
  onValueChange,
  placeholder = "Add a tag",
  "aria-describedby": describedBy,
  "aria-invalid": invalid,
  quiet = false,
}: {
  label?: string;
  id?: string;
  value: string[];
  onValueChange: (value: string[]) => void;
  placeholder?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
  /** Edited in place: no box and no remove buttons until it is hovered or focused (always shown on touch). */
  quiet?: boolean;
}) {
  const generated = useId();
  const inputId = id ?? generated;
  const [draft, setDraft] = useState("");
  const [said, setSaid] = useState("");

  function commit() {
    const next = draft.trim();
    setDraft("");
    if (!next || value.includes(next)) return;
    onValueChange([...value, next]);
    setSaid(`Added tag ${next}`);
  }

  function remove(tag: string) {
    onValueChange(value.filter((item) => item !== tag));
    setSaid(`Removed tag ${tag}`);
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      commit();
    } else if (event.key === "Backspace" && draft === "" && value.length > 0) {
      remove(value[value.length - 1]!);
    }
  }

  return (
    <div className="grid gap-2" data-slot="tag-input">
      {label ? <label htmlFor={inputId} className="text-sm">{label}</label> : null}
      <div className={cn("group flex min-h-11 flex-wrap items-center gap-2 rounded-control border border-line bg-surface px-2 py-1.5 focus-within:border-[var(--focus-outline)]", quiet && "border-transparent bg-transparent can-hover:hover:border-line")}>
        {value.length > 0 ? (
          <ul className="contents">
            {value.map((tag) => (
              <li key={tag} className="inline-flex h-8 items-center gap-0.5 rounded-pill bg-surface-muted pr-0.5 pl-2.5 text-sm">
                {tag}
                <button
                  type="button"
                  aria-label={`Remove tag ${tag}`}
                  onClick={() => remove(tag)}
                  className={cn("relative grid size-7 place-items-center rounded-pill text-secondary can-hover:hover:text-foreground after:absolute after:-inset-1.5", quiet && "opacity-0 group-focus-within:opacity-100 can-hover:group-hover:opacity-100 [@media(hover:none)]:opacity-100")}
                >
                  <X aria-hidden className="size-3.5" strokeWidth={1.75} />
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        <input
          id={inputId}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={onKeyDown}
          onBlur={commit}
          placeholder={value.length === 0 ? placeholder : ""}
          aria-describedby={describedBy}
          aria-invalid={invalid}
          className="min-w-24 flex-1 bg-transparent py-1 text-sm outline-none"
        />
      </div>
      <div className="sr-only" role="status" aria-live="polite">{said}</div>
    </div>
  );
}
